import {
  thingsOf,
  type Crossing,
  type CountryFacts,
  type DocsScreen,
  type Money,
  type MoneyRange,
  type Plan,
  type PlanStop,
} from '@trippath/shared';

import { iataOf } from './airports';

/** `2026-10-21` from `2026-10-18` and 3. */
export function plusDays(iso: string, days: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/** `18–28 Oct`, `28 Oct – 11 Nov`: two days of the trip, the year understood. */
export function dayRange(first: string, last: string, language: string): string {
  const format =
    ranges.get(language) ??
    new Intl.DateTimeFormat(language === 'et' ? 'et-EE' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  ranges.set(language, format);
  return format.formatRange(new Date(`${first}T00:00:00Z`), new Date(`${last}T00:00:00Z`));
}

/** Each language's way of saying a stretch of days, made once: making one is slow, and a page
 * says many, each time it is drawn. */
const ranges = new Map<string, Intl.DateTimeFormat>();

const sum = (ranges: MoneyRange[], currency: string): MoneyRange => ({
  min: { currency, minor: ranges.reduce((all, r) => all + r.min.minor, 0) },
  max: { currency, minor: ranges.reduce((all, r) => all + r.max.minor, 0) },
});

const times = (range: MoneyRange, n: number): MoneyRange => ({
  min: { ...range.min, minor: range.min.minor * n },
  max: { ...range.max, minor: range.max.minor * n },
});

/** A stop's nights booked: its stays' nights, no more than it has. */
export function bookedNights(stop: PlanStop): number {
  const booked = stop.bookings
    .filter((b) => b.kind === 'stay')
    .reduce((all, b) => all + b.nights, 0);
  return Math.min(stop.days, booked);
}

/** A country of the trip as its page says it: its stops, days, money by kind and its entries. */
export interface CountryView {
  country: string;
  stops: PlanStop[];
  first: string;
  last: string;
  nights: number;
  booked: number;
  stays: MoneyRange;
  food: MoneyRange;
  local: MoneyRange;
  activities: MoneyRange;
  paid: Money;
  toPay: Money;
  /** The airport or station it is entered at, `BKK`, when the way says so. */
  enters: string | null;
  crossings: Crossing[];
  facts: CountryFacts | null;
}

/** The trip's countries in route order, each with what its page says of it. */
export function countryViews(
  plan: Plan,
  docs: DocsScreen | null,
  facts: CountryFacts[],
): CountryView[] {
  const currency = plan.trip.currency;
  const groups = new Map<string, { first: PlanStop; last: PlanStop; all: PlanStop[] }>();
  // A country only changed in is one too: its border, money and days said; no stop of its own.
  for (const stop of plan.stops) {
    const group = groups.get(stop.country);
    const here = stop.transfer ? [] : [stop];
    if (group) {
      group.last = stop;
      group.all.push(...here);
    } else groups.set(stop.country, { first: stop, last: stop, all: here });
  }
  const add = (pick: (stop: PlanStop) => MoneyRange, here: PlanStop[]) =>
    sum(here.map(pick), currency);
  return [...groups].map(([country, { first, last, all: here }]) => {
    const before = plan.stops[plan.stops.indexOf(first) - 1];
    return {
      country,
      stops: here,
      first: first.start,
      last: plusDays(last.start, last.days),
      nights: here.reduce((all, stop) => all + stop.days, 0),
      booked: here.reduce((all, stop) => all + bookedNights(stop), 0),
      stays: add((stop) => times(stop.accommodation, stop.days), here),
      food: add((stop) => times(stop.food, stop.days), here),
      local: add((stop) => times(stop.local, stop.days), here),
      activities: add(thingsOf, here),
      paid: { currency, minor: here.reduce((all, stop) => all + stop.cost.paid.minor, 0) },
      toPay: { currency, minor: here.reduce((all, stop) => all + stop.cost.toPay.minor, 0) },
      enters: iataOf(before?.out?.to?.name),
      crossings: docs ? docs.crossings.filter((c) => c.country === country) : [],
      facts: facts.find((f) => f.country === country) ?? null,
    };
  });
}

/** A zone's offset from UTC at noon of a day, in minutes: Bangkok 420. */
export function offsetMinutes(zone: string, day: string): number {
  const key = `${zone} ${day}`;
  const known = offsets.get(key);
  if (known !== undefined) return known;
  const worked = workOffset(zone, day);
  offsets.set(key, worked);
  return worked;
}

/** Each clock's offset on each day, worked out once: asked for again each time a page is
 * drawn, and slow to work out. */
const offsets = new Map<string, number>();

function workOffset(zone: string, day: string): number {
  const at = new Date(`${day}T12:00:00Z`);
  const wall = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(at);
  const part = (type: string) => Number(wall.find((p) => p.type === type)?.value);
  const local = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
  );
  return Math.round((local - at.getTime()) / 60_000);
}

/** `UTC+7`, `UTC+5:30`, `UTC`: an offset as it is said. */
export function utcOffset(minutes: number): string {
  if (minutes === 0) return 'UTC';
  const sign = minutes > 0 ? '+' : '−';
  const whole = Math.abs(minutes);
  const rest = whole % 60;
  return `UTC${sign}${String(Math.floor(whole / 60))}${rest === 0 ? '' : `:${String(rest).padStart(2, '0')}`}`;
}
